import { Root } from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import { XYChart } from '@amcharts/amcharts5/xy';
import { Injectable } from '@angular/core';
import {
    isDate,
    isEmpty
} from 'lodash';
import {
    datiqGetGraphDateCategoryDaily,
    datiqGetGraphDateCategoryMonthly,
    datiqGetGraphDateCategoryWeekly,
    datiqGetGraphDateCategoryYearly,
    DatiqGraphAxisDateTypes,
    DatiqGraphSeriesDataTypes,
    emptyDatiqGraphAxisX,
    IDatiqGraph,
    IDatiqGraphSeries,
    IDatiqGraphSeriesData,
} from '../models';
import { DatiqAmCharts5SeriesColumnBuilder } from './datiq-amcharts-5-series-column.builder';

@Injectable({
    providedIn: 'root',
})
export class DatiqAmcharts5SeriesColumnService {
    constructor() {}

    create(
        root: Root,
        chart: XYChart,
        seriesData: IDatiqGraphSeries,
        settings: IDatiqGraph
    ): am5xy.XYChart {
        const series: am5xy.ColumnSeries = chart?.series.push(
            am5xy.ColumnSeries.new(root, {
                ...this.createIntialLineSettings(seriesData, chart),
            })
        );
        const builder: DatiqAmCharts5SeriesColumnBuilder =
            new DatiqAmCharts5SeriesColumnBuilder();

        this.setData(seriesData, series, settings, chart);

        builder
            .setDefault(series)
            .setColor(series, seriesData)
            .setPattern(series, root, seriesData)
            .setTooltip(series, root, seriesData, settings);

        return chart;
    }
    createIntialLineSettings(
        seriesData: any,
        chart: XYChart
    ): am5xy.IColumnSeriesSettings {
        return {
            name: seriesData.id,
            xAxis: chart.xAxes.getIndex(0),
            yAxis: chart.yAxes.getIndex(0),
            valueYField: 'y',
            categoryXField: 'x',
        };
    }

    private setData(
        seriesData: IDatiqGraphSeries,
        series: am5xy.ColumnSeries,
        settings: IDatiqGraph,
        chart: XYChart
    ) {
        const data: unknown[] = this.mapDataList(seriesData.data, settings);
        series.data.setAll(data);
        chart.xAxes.getIndex(0).data.setAll(data);

        return this;
    }
    mapDataList(
        dataList: IDatiqGraphSeriesData[],
        settings: IDatiqGraph
    ): unknown[] {
        return dataList?.map((data: IDatiqGraphSeriesData): unknown => {
            return this.mapData(data, settings);
        });
    }

    private mapData(
        data: IDatiqGraphSeriesData,
        settings: IDatiqGraph
    ): unknown {
        return !isEmpty(data)
            ? Object.entries(data).reduce(
                  (
                      mappedData: object,
                      [key, value]: [string, DatiqGraphSeriesDataTypes]
                  ) => {
                      return {
                          ...mappedData,
                          [key]: this.mapDataValue(key, value, settings),
                      };
                  },
                  data
              )
            : data;
    }
    mapDataValue(
        key: string,
        value: DatiqGraphSeriesDataTypes,
        settings: IDatiqGraph
    ): any {
        return this.shouldMapToDateCategory(key, value)
            ? this.mapToDateCategory(value, settings)
            : isDate(value)
            ? value.getTime()
            : value;
    }
    mapToDateCategory(
        value: DatiqGraphSeriesDataTypes,
        settings: IDatiqGraph
    ): any {
        const dateValue: Date = value as Date;
        const xAxisData = settings?.xAxes?.[0] ?? { ...emptyDatiqGraphAxisX };
        const label: string =
            xAxisData.dateValueType === DatiqGraphAxisDateTypes.Daily
                ? datiqGetGraphDateCategoryDaily(dateValue)
                : xAxisData.dateValueType === DatiqGraphAxisDateTypes.Weekly
                ? datiqGetGraphDateCategoryWeekly(dateValue)
                : xAxisData.dateValueType === DatiqGraphAxisDateTypes.Monthly
                ? datiqGetGraphDateCategoryMonthly(dateValue)
                : xAxisData.dateValueType === DatiqGraphAxisDateTypes.Yearly
                ? datiqGetGraphDateCategoryYearly(dateValue)
                : datiqGetGraphDateCategoryDaily(dateValue);

        return label;
    }
    shouldMapToDateCategory(
        key: string,
        value: DatiqGraphSeriesDataTypes
    ): boolean {
        const correctKey = key === 'x';
        const isDateType = isDate(value);

        return correctKey && isDateType;
    }
}
